import { useEffect, useState, type ReactElement } from 'react';
import {
  WORKSPACE_TYPES,
  type Workspace,
  type WorkspaceProfilePatch,
  type WorkspaceRole,
  type WorkspaceType,
  type WorkspaceVisibility,
} from '@molt/shared';
import { Button, CardSurface, Notice, TextField } from '@/components';
import { cn } from '@/lib/cn';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { WorkspaceDeleteDialog } from './WorkspaceDeleteDialog';

/**
 * `FE-80.1` — a module constant cannot call `t()` where it sits, so each of
 * these records holds the KEY and the render resolves it (the
 * `ALL_WORKSPACES_KEY` shape).
 *
 * `WORKSPACE_TYPE_KEYS` replaces a `workspaceTypeLabel()` that derived the
 * English from the wire value by string-munging (`human_resources` → `Human
 * Resources`). That is untranslatable in principle: no served catalogue can
 * reach a string that does not exist until render.
 *
 * ⚠ The two records point into **different namespaces on purpose**, and
 * collapsing them is the thing a later reader will try.
 * `WORKSPACE_VISIBILITY_KEYS` **reuses** `workspaceMembers.settings.visibility.*`
 * rather than declaring a third `Private`/`Public` pair, because the route one
 * panel away SPEAKS `Workspace visibility set to {visibility}.` about this very
 * toggle — the word spoken and the word drawn must not diverge in any language.
 * The catalogue says so in a comment at those two keys.
 *
 * ⚠ The record **key** is the wire value and stays literal — it is what
 * `onVisibilityChange` sends and what `patch.workspaceType` carries. Only the
 * resolved value is copy; routing a sent value earns a 422 that reads like an
 * auth bug.
 */
const WORKSPACE_TYPE_KEYS: Record<WorkspaceType, string> = {
  engineering: 'workspaceSettings.type.engineering',
  marketing: 'workspaceSettings.type.marketing',
  education: 'workspaceSettings.type.education',
  sales: 'workspaceSettings.type.sales',
  support: 'workspaceSettings.type.support',
  human_resources: 'workspaceSettings.type.humanResources',
  operations: 'workspaceSettings.type.operations',
  finance: 'workspaceSettings.type.finance',
  product_management: 'workspaceSettings.type.productManagement',
  design: 'workspaceSettings.type.design',
  legal: 'workspaceSettings.type.legal',
  other: 'workspaceSettings.type.other',
};

const WORKSPACE_VISIBILITY_KEYS: Record<WorkspaceVisibility, string> = {
  private: 'workspaceMembers.settings.visibility.private',
  public: 'workspaceMembers.settings.visibility.public',
};

export interface WorkspaceSettingsPanelProps {
  workspace: Workspace;
  viewerRole: WorkspaceRole | null;
  /**
   * SA-9 — the admin capability, when the caller has one without an admin row
   * (a platform superadmin, D10). Omitted, it is `viewerRole === 'admin'`.
   * Leaving still reads `viewerRole`: there is no row to leave.
   */
  viewerCanAdminister?: boolean | undefined;
  onProfileChange: (patch: WorkspaceProfilePatch) => void;
  onVisibilityChange: (visibility: WorkspaceVisibility) => void;
  onDeleteWorkspace: (confirmName: string) => void;
  onLeaveWorkspace: () => void;
  busy?: {
    profile?: boolean;
    visibility?: boolean;
    delete?: boolean;
    leave?: boolean;
  };
  error?: {
    profile?: string;
    visibility?: string;
    delete?: string;
    leave?: string;
  };
  className?: string;
}

export function WorkspaceSettingsPanel({
  workspace,
  viewerRole,
  viewerCanAdminister,
  onProfileChange,
  onVisibilityChange,
  onDeleteWorkspace,
  onLeaveWorkspace,
  busy,
  error,
  className,
}: WorkspaceSettingsPanelProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const canAdmin = viewerCanAdminister ?? viewerRole === 'admin';
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [draft, setDraft] = useState(() => draftFromWorkspace(workspace));

  useEffect(() => {
    setDraft(draftFromWorkspace(workspace));
  }, [workspace]);

  const profilePatch = buildProfilePatch(workspace, draft);
  const profileChanged = Object.keys(profilePatch).length > 0;

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      <section
        aria-labelledby="workspace-profile-heading"
        className="flex flex-col gap-3"
      >
        <h2 id="workspace-profile-heading" className="text-base font-semibold text-ink">
          {t('workspaceSettings.profile.heading')}
        </h2>
        <CardSurface variant="sheet" className="flex flex-col gap-3 p-4">
          {error?.profile !== undefined ? (
            <Notice tone="bad" body={error.profile} />
          ) : null}
          <div className="grid gap-3 md:grid-cols-2">
            <TextField
              label={t('workspaceSettings.profile.nameLabel')}
              value={draft.name}
              readOnly={!canAdmin}
              onChange={(event) => setDraft({ ...draft, name: event.target.value })}
            />
            <TextField
              label={t('workspaceSettings.profile.shortNameLabel')}
              value={draft.shortName}
              readOnly={!canAdmin}
              hint={t('workspaceSettings.profile.shortNameHint')}
              onChange={(event) => setDraft({ ...draft, shortName: event.target.value })}
            />
          </div>
          <TextField
            label={t('workspaceSettings.profile.descriptionLabel')}
            multiline
            value={draft.description}
            readOnly={!canAdmin}
            onChange={(event) => setDraft({ ...draft, description: event.target.value })}
          />
          <div className="grid gap-3 md:grid-cols-2">
            <TextField
              label={t('workspaceSettings.profile.websiteLabel')}
              value={draft.websiteUrl}
              readOnly={!canAdmin}
              onChange={(event) => setDraft({ ...draft, websiteUrl: event.target.value })}
            />
            <TextField
              label={t('workspaceSettings.profile.logoLabel')}
              value={draft.logoUrl}
              readOnly={!canAdmin}
              hint={t('workspaceSettings.profile.logoHint')}
              onChange={(event) => setDraft({ ...draft, logoUrl: event.target.value })}
            />
          </div>
          <label className="flex flex-col gap-1 text-sm font-medium text-ink">
            {t('workspaceSettings.profile.typeLabel')}
            <select
              className="h-9 rounded-card border border-border-strong bg-surface-raised px-3 text-ink"
              value={draft.workspaceType}
              disabled={!canAdmin}
              onChange={(event) =>
                setDraft({
                  ...draft,
                  workspaceType: event.target.value as WorkspaceType | '',
                })
              }
            >
              <option value="">{t('workspaceSettings.type.none')}</option>
              {WORKSPACE_TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(WORKSPACE_TYPE_KEYS[type])}
                </option>
              ))}
            </select>
          </label>
          {canAdmin ? (
            <div>
              <Button
                variant="primary"
                busy={busy?.profile ?? false}
                disabled={!profileChanged}
                disabledReason={t('workspaceSettings.profile.saveDisabled')}
                onClick={() => onProfileChange(profilePatch)}
              >
                {t('workspaceSettings.profile.save')}
              </Button>
            </div>
          ) : (
            <Notice tone="locked" body={t('workspaceSettings.profile.locked')} />
          )}
        </CardSurface>
      </section>

      <section
        aria-labelledby="workspace-visibility-heading"
        className="flex flex-col gap-3"
      >
        <h2
          id="workspace-visibility-heading"
          className="text-base font-semibold text-ink"
        >
          {t('workspaceSettings.visibility.heading')}
        </h2>
        <CardSurface variant="sheet" className="flex flex-col gap-3 p-4">
          {error?.visibility !== undefined ? (
            <Notice tone="bad" body={error.visibility} />
          ) : null}
          <p className="text-sm text-ink-muted">
            {t('workspaceSettings.visibility.description')}
          </p>
          <div
            className="flex flex-wrap gap-2"
            role="group"
            aria-label={t('workspaceSettings.visibility.heading')}
          >
            {(['private', 'public'] as const).map((visibility) => (
              <Button
                key={visibility}
                variant="toggle"
                pressed={workspace.visibility === visibility}
                disabled={!canAdmin}
                disabledReason={t('workspaceSettings.visibility.locked')}
                busy={busy?.visibility === true && workspace.visibility !== visibility}
                onClick={() => onVisibilityChange(visibility)}
              >
                {t(WORKSPACE_VISIBILITY_KEYS[visibility])}
              </Button>
            ))}
          </div>
        </CardSurface>
      </section>

      <section
        aria-labelledby="workspace-departure-heading"
        className="flex flex-col gap-3"
      >
        <h2 id="workspace-departure-heading" className="text-base font-semibold text-ink">
          {t('workspaceSettings.leave.heading')}
        </h2>
        <CardSurface variant="sheet" className="flex flex-col gap-3 p-4">
          {error?.leave !== undefined ? <Notice tone="bad" body={error.leave} /> : null}
          <p className="text-sm text-ink-muted">
            {t('workspaceSettings.leave.description')}
          </p>
          <div>
            <Button
              variant="secondary"
              busy={busy?.leave ?? false}
              disabled={viewerRole === null}
              disabledReason={t('workspaceSettings.leave.pending')}
              onClick={onLeaveWorkspace}
            >
              {t('workspaceSettings.leave.action')}
            </Button>
          </div>
        </CardSurface>
      </section>

      {canAdmin ? (
        <section
          aria-labelledby="workspace-delete-heading"
          className="flex flex-col gap-3"
        >
          <h2 id="workspace-delete-heading" className="text-base font-semibold text-ink">
            {t('workspaceSettings.delete.heading')}
          </h2>
          <CardSurface variant="sheet" className="flex flex-col gap-3 p-4">
            <p className="text-sm text-ink-muted">
              {t('workspaceSettings.delete.description')}
            </p>
            <div>
              <Button variant="danger" onClick={() => setDeleteOpen(true)}>
                {t('workspaceSettings.delete.action')}
              </Button>
            </div>
          </CardSurface>
          <WorkspaceDeleteDialog
            open={deleteOpen}
            workspaceName={workspace.name}
            busy={busy?.delete ?? false}
            error={error?.delete ?? null}
            onConfirm={onDeleteWorkspace}
            onCancel={() => setDeleteOpen(false)}
          />
        </section>
      ) : null}
    </div>
  );
}

interface WorkspaceDraft {
  name: string;
  shortName: string;
  description: string;
  websiteUrl: string;
  logoUrl: string;
  workspaceType: WorkspaceType | '';
}

function draftFromWorkspace(workspace: Workspace): WorkspaceDraft {
  return {
    name: workspace.name,
    shortName: workspace.shortName,
    description: workspace.description ?? '',
    websiteUrl: workspace.websiteUrl ?? '',
    logoUrl: workspace.logoUrl ?? '',
    workspaceType: workspace.workspaceType ?? '',
  };
}

function buildProfilePatch(
  workspace: Workspace,
  draft: WorkspaceDraft,
): WorkspaceProfilePatch {
  const patch: WorkspaceProfilePatch = {};
  const description = emptyToNull(draft.description);
  const websiteUrl = emptyToNull(draft.websiteUrl);
  const logoUrl = emptyToNull(draft.logoUrl);
  const workspaceType = draft.workspaceType === '' ? null : draft.workspaceType;

  if (draft.name !== workspace.name) patch.name = draft.name;
  if (draft.shortName !== workspace.shortName) patch.shortName = draft.shortName;
  if (description !== workspace.description) patch.description = description;
  if (websiteUrl !== workspace.websiteUrl) patch.websiteUrl = websiteUrl;
  if (logoUrl !== workspace.logoUrl) patch.logoUrl = logoUrl;
  if (workspaceType !== workspace.workspaceType) patch.workspaceType = workspaceType;

  return patch;
}

function emptyToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}
